import type { ModalPosition, ModalSize } from '../components/Modal/utils/types';
import { OPENX_AGENT_OPEN_STORAGE_KEY, OPENX_AGENT_POSITION_STORAGE_KEY, OPENX_AGENT_SIZE_STORAGE_KEY } from './consts';

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const isPosition = (value: unknown): value is ModalPosition => {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Partial<ModalPosition>;
  return isFiniteNumber(candidate.x) && isFiniteNumber(candidate.y);
};

const isSize = (value: unknown): value is ModalSize => {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Partial<ModalSize>;
  return isFiniteNumber(candidate.width) && isFiniteNumber(candidate.height);
};

const readStoredJson = <T>(key: string, isValid: (value: unknown) => value is T): T | null => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeStoredJson = (key: string, value: unknown): void => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable
  }
};

export const readStoredPosition = (key: string = OPENX_AGENT_POSITION_STORAGE_KEY): ModalPosition | null =>
  readStoredJson(key, isPosition);

export const writeStoredPosition = (position: ModalPosition, key: string = OPENX_AGENT_POSITION_STORAGE_KEY): void =>
  writeStoredJson(key, position);

export const readStoredSize = (key: string = OPENX_AGENT_SIZE_STORAGE_KEY): ModalSize | null =>
  readStoredJson(key, isSize);

export const writeStoredSize = (size: ModalSize, key: string = OPENX_AGENT_SIZE_STORAGE_KEY): void =>
  writeStoredJson(key, size);

export const readStoredOpen = (key: string = OPENX_AGENT_OPEN_STORAGE_KEY): boolean => {
  if (typeof window === 'undefined') return false;

  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

export const writeStoredOpen = (isOpen: boolean, key: string = OPENX_AGENT_OPEN_STORAGE_KEY): void => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(key, isOpen ? 'true' : 'false');
  } catch {
    // storage unavailable
  }
};
